import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import FadeIn from "@/components/FadeIn";
import SkylineDivider from "@/components/SkylineDivider";
import { IMAGES } from "@/lib/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OFFERINGS = [
  {
    title: "Yoga & Wellness Classes",
    desc: "Slow, grounded movement and breathwork — from the studio to the hilltop when the weather cooperates. Honoring nervous-system safety, no mirrors, no pressure, no performance.",
    img: IMAGES.imgHillDope,
  },
  {
    title: "Healing-Centered Workshops",
    desc: "Small-group sessions on grief, burnout, regulation, and rebuilding from the ground up.",
    img: IMAGES.fieldPicnic,
  },
  {
    title: "Private & Group Retreats",
    desc: "Weekend or weeklong gatherings — facilitated, fully held, and limited to small numbers by design.",
    img: IMAGES.newView09,
  },
];

export default function NewLeaf() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.endsWith("/interest")) {
      setTimeout(() => document.getElementById("interest")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, [location.pathname]);
  const [form, setForm] = useState({ name: "", email: "", interest_area: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/wellness-interest`, form);
      toast.success("Thank you — we'll be in touch as offerings open.");
      setForm({ name: "", email: "", interest_area: "", message: "" });
    } catch (err) {
      toast.error("Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="newleaf-page">
      <section className="container-x pt-12 lg:pt-24 pb-12" data-testid="newleaf-hero">
        <FadeIn>
          <img
            src="/new-leaf-logo-sage.png"
            alt="New Leaf Wellness Collective"
            className="h-24 lg:h-32 w-auto -ml-2 mb-8"
          />
          <p className="overline mb-5">Coming to the farm</p>
        </FadeIn>
        <FadeIn delay={120}>
          <h1 className="font-serif text-5xl lg:text-7xl text-moss font-light leading-[1.02] tracking-tight max-w-4xl">
            Healing, <em className="not-italic text-clay">with room to breathe.</em>
          </h1>
        </FadeIn>
        <FadeIn delay={240}>
          <p className="mt-8 max-w-2xl font-serif italic text-xl lg:text-2xl text-clay leading-[1.5]">
            "I am the vine; you are the branches… he prunes so that we may bear much fruit."
          </p>
          <p className="mt-3 overline">— John 15:1–5</p>
        </FadeIn>
        <FadeIn delay={360}>
          <p className="mt-10 max-w-2xl text-lg text-coal/70 leading-[1.85]">
            New Leaf Wellness Collective is the wellness branch of Iron Talon Farm. Soon, this is
            where restorative yoga, small groups, and gentle workshops will take root —
            grounded in the same quiet land that holds the cottages.
          </p>
        </FadeIn>
      </section>

      <FadeIn className="container-x">
        <img src={IMAGES.mlsYogaMats} alt="The wellness space at Iron Talon Farm — yoga mats laid out on wide-plank floors" className="w-full aspect-[16/9] lg:aspect-[21/9] object-cover" />
      </FadeIn>

      {/* PHILOSOPHY */}
      <section className="container-narrow py-24 lg:py-32 text-center" data-testid="newleaf-philosophy">
        <FadeIn><p className="overline mb-6">Our Approach</p></FadeIn>
        <FadeIn delay={120}>
          <p className="font-serif text-3xl lg:text-5xl text-moss font-light leading-[1.25] tracking-tight">
            Slow is sacred. The nervous system can't be hurried —
            so we don't try.
          </p>
        </FadeIn>
        <FadeIn delay={260}>
          <p className="mt-10 text-coal/70 text-lg leading-[1.85] max-w-2xl mx-auto">
            Every offering is built around what deeply attentive wellness care actually looks like:
            choice, safety, transparency, and the simple kindness of <em>not too much, not too fast</em>.
          </p>
        </FadeIn>
      </section>

      {/* OFFERINGS */}
      <section className="container-x pb-24 lg:pb-32" data-testid="newleaf-offerings">
        <FadeIn>
          <p className="overline mb-5">Offerings</p>
          <h2 className="font-serif text-4xl lg:text-5xl text-moss font-light leading-tight tracking-tight max-w-2xl">
            Practices held with care.
          </h2>
        </FadeIn>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
          {OFFERINGS.map((o, i) => (
            <FadeIn key={o.title} delay={i * 120}>
              <div className="group bg-oat overflow-hidden h-full flex flex-col">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={o.img} alt={o.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                </div>
                <div className="p-8 lg:p-10 flex-1">
                  <h3 className="font-serif text-2xl lg:text-3xl text-moss font-medium leading-tight">{o.title}</h3>
                  <p className="mt-5 text-coal/70 leading-[1.85]">{o.desc}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      <SkylineDivider variant="moss" height={90} opacity={0.5} className="bg-cream" data-testid="newleaf-skyline-divider" />

      {/* INTEREST FORM */}
      <section id="interest" className="bg-moss text-cream py-24 lg:py-32 scroll-mt-24" data-testid="newleaf-interest-section">
        <div className="container-narrow">
          <FadeIn>
            <img
              src="/new-leaf-logo-light.png"
              alt="New Leaf Wellness Collective"
              className="h-20 lg:h-24 w-auto mx-auto mb-8 opacity-80"
            />
            <p className="overline text-cream/60 mb-5 text-center">Stay close</p>
            <h2 className="font-serif text-4xl lg:text-6xl font-light leading-[1.05] tracking-tight text-center">
              Be the first to know.
            </h2>
            <p className="mt-6 text-cream/75 text-lg leading-[1.85] text-center max-w-xl mx-auto">
              Sessions and retreats are limited by design. Share a note and we'll reach out
              when an offering aligned with your interest opens.
            </p>
          </FadeIn>

          <FadeIn delay={160}>
            <form onSubmit={onSubmit} className="mt-14 space-y-10" data-testid="wellness-interest-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-cream/60 mb-2">Name</label>
                  <input required name="name" value={form.name} onChange={onChange} className="w-full bg-transparent border-0 border-b border-cream/30 focus:border-cream py-3 outline-none text-cream placeholder:text-cream/40" data-testid="wellness-name-input" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-cream/60 mb-2">Email</label>
                  <input required type="email" name="email" value={form.email} onChange={onChange} className="w-full bg-transparent border-0 border-b border-cream/30 focus:border-cream py-3 outline-none text-cream placeholder:text-cream/40" data-testid="wellness-email-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-cream/60 mb-2">What draws you?</label>
                  <select name="interest_area" value={form.interest_area} onChange={onChange} className="w-full bg-transparent border-0 border-b border-cream/30 focus:border-cream py-3 outline-none text-cream" data-testid="wellness-interest-select">
                    <option value="" style={{ backgroundColor: "#fefcf6", color: "#1a1a1a" }}>Select an area of interest</option>
                    <option style={{ backgroundColor: "#fefcf6", color: "#1a1a1a" }}>Yoga & wellness classes</option>
                    <option style={{ backgroundColor: "#fefcf6", color: "#1a1a1a" }}>Restorative practice days</option>
                    <option style={{ backgroundColor: "#fefcf6", color: "#1a1a1a" }}>Healing-centered workshops</option>
                    <option style={{ backgroundColor: "#fefcf6", color: "#1a1a1a" }}>Private or group retreats</option>
                    <option style={{ backgroundColor: "#fefcf6", color: "#1a1a1a" }}>Not sure yet — keep me in the loop</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-cream/60 mb-2">Anything you'd like to share?</label>
                  <textarea name="message" rows={4} value={form.message} onChange={onChange} className="w-full bg-transparent border-0 border-b border-cream/30 focus:border-cream py-3 outline-none text-cream placeholder:text-cream/40 resize-none" data-testid="wellness-message-input" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-ghost-light disabled:opacity-50" data-testid="wellness-submit-btn">
                {loading ? "Sending…" : "Send"}
              </button>
            </form>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
